import { oc } from "@orpc/contract";
import { z } from "zod";
import { IntegrationAvailabilitySchema, IntegrationKey } from "./base";
import {
  FamousIntegrationSchema,
  FamousIntegrationSettingsSchemaWithoutBase,
} from "./famous";
import {
  PlaidIntegrationSchema,
  PlaidIntegrationSettingsSchemaWithoutBase,
  plaidContracts,
} from "./plaid.contract";
import {
  PaymentsIntegrationSchema,
  PaymentsIntegrationSettingsSchemaWithoutBase,
  paymentsIntegrationContracts,
} from "./payments.contract";

/**
 * Discriminated union of all integration types
 */
export const IntegrationSchema = z.discriminatedUnion("key", [
  FamousIntegrationSchema,
  PlaidIntegrationSchema,
  PaymentsIntegrationSchema,
]);

/**
 * Integration DTO type
 */
export type IntegrationDTO = z.infer<typeof IntegrationSchema>;

/**
 * Update input schema for integrations
 */
export const IntegrationUpdateInputSchema = z.discriminatedUnion("key", [
  FamousIntegrationSettingsSchemaWithoutBase,
  PlaidIntegrationSettingsSchemaWithoutBase,
  PaymentsIntegrationSettingsSchemaWithoutBase,
]);

// Contracts
const listIntegrations = oc
  .output(
    z.object({
      installed: z
        .partialRecord(z.enum(IntegrationKey), IntegrationSchema)
        .prefault({}),
      availability: z
        .partialRecord(z.enum(IntegrationKey), IntegrationAvailabilitySchema)
        .prefault({}),
    }),
  )
  .route({
    method: "GET",
  });

const getIntegration = oc
  .input(z.object({ key: z.enum(IntegrationKey) }))
  .output(IntegrationSchema)
  .route({ method: "GET" });

const updateIntegration = oc
  .input(IntegrationUpdateInputSchema)
  .output(IntegrationSchema);

export const integrationsContracts = {
  list: listIntegrations,
  get: getIntegration,
  update: updateIntegration,
  plaid: plaidContracts,
  payments: paymentsIntegrationContracts,
} as const;

export type IntegrationsContracts = typeof integrationsContracts;

// Re-export base types and schemas
export {
  IntegrationKey,
  IntegrationBaseSchema,
  IntegrationAvailabilitySchema,
} from "./base";
export type { IntegrationAvailability } from "./base";

// Re-export Famous types and schemas
export {
  FamousIntegrationSettingsSchema,
  FamousIntegrationSchema,
} from "./famous";
export type { FamousIntegrationSettings, FamousIntegration } from "./famous";

// Re-export Plaid types, schemas, and contracts
export {
  PlaidIntegrationSettingsSchema,
  PlaidBankAccountSchema,
  PlaidIntegrationSchema,
  plaidContracts,
} from "./plaid.contract";
export type {
  PlaidIntegrationSettings,
  PlaidBankAccount,
  PlaidIntegration,
  PlaidContracts,
} from "./plaid.contract";

// Re-export Payments types, schemas, and contracts
export {
  PaymentsIntegrationSettingsSchema,
  PaymentAccountSchema,
  PaymentsIntegrationSchema,
  CheckbookBankStatusSchema,
  BankAccountTypeSchema,
  CheckbookMarketplaceUserSchema,
  paymentsIntegrationContracts,
  isPaymentsIntegration,
  hasCheckbookCredentials,
  // KYB/KYC schemas for Checkbook onboarding
  USStateSchema,
  IndustryCodeSchema,
  CitizenshipStatusSchema,
  AddressSchema,
  BusinessInfoSchema,
  PrincipalOfficerInfoSchema,
  CheckbookOnboardingInfoSchema,
  // Display label arrays for form dropdowns
  US_STATES,
  INDUSTRY_CODES,
  CITIZENSHIP_OPTIONS,
} from "./payments.contract";
export type {
  PaymentsIntegrationSettings,
  PaymentAccount,
  PaymentsIntegration,
  CheckbookBankStatus,
  BankAccountType,
  CheckbookMarketplaceUser,
  PaymentsIntegrationContracts,
  // KYB/KYC types for Checkbook onboarding
  USState,
  IndustryCode,
  CitizenshipStatus,
  Address,
  BusinessInfo,
  PrincipalOfficerInfo,
  CheckbookOnboardingInfo,
} from "./payments.contract";
